import * as React from 'react'
import { Sparkles } from 'lucide-react'

import type { CommercialInsight } from '@sdr-agend/admin-schemas'
import { formatRelativeTime } from '@/lib/format'

/**
 * "Insight Principal" (Story 10.8, 2026-07-30, user request, AC5) — texto
 * curto gerado por LLM, cacheado e regenerado no máximo a cada 3h por
 * `dashboard-insight-scheduler.worker.ts`. Sempre rotulado como "gerado por
 * IA" (nunca apresentado como fato absoluto — mitigação de risco de
 * alucinação, ver Story 10.8 Riscos).
 *
 * **Divergência de período deliberada (AC5):** o texto SEMPRE comenta o mês
 * corrente até agora, independente do período selecionado nos filtros do
 * dashboard (a tabela ao lado SIM respeita o filtro) — por isso o rótulo
 * "Insight sobre o mês corrente" é explícito, não um detalhe de rodapé.
 */
export interface CommercialInsightCalloutProps {
  data: CommercialInsight | null
}

export function CommercialInsightCallout({ data }: CommercialInsightCalloutProps): React.ReactElement {
  if (data === null) {
    return (
      <div
        className="flex h-16 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="commercial-insight-empty"
      >
        Insight ainda não gerado — aguardando a primeira sincronização.
      </div>
    )
  }

  return (
    <div
      className="flex items-start gap-3 rounded-md border bg-muted/40 p-3"
      data-testid="commercial-insight-callout"
    >
      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" aria-hidden="true" />
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
          <span className="rounded-full bg-violet-100 px-2 py-0.5 font-medium text-violet-700 dark:bg-violet-950 dark:text-violet-300">
            Gerado por IA
          </span>
          <span>Insight sobre o mês corrente</span>
          <span data-testid="commercial-insight-generated-at">
            atualizado {formatRelativeTime(data.generatedAt)}
          </span>
        </div>
        <p className="text-sm">{data.text}</p>
      </div>
    </div>
  )
}

export default CommercialInsightCallout
